import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaEdit, FaTrashAlt, FaCalendarAlt, FaInfoCircle } from "react-icons/fa";
import * as XLSX from "xlsx";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { formatFullName, formatPascal, formatPhone, formatEmail, getServiceShortcodeFromCategories } from "../utils/formatters";
import BusinessLeadsTable from "./BusinessLeadsTable";
import TrainingLeadsTable from "./TrainingLeadsTable";
import JobLeadsTable from "./JobLeadsTable";
import InternshipLeadsTable from "./InternshipLeadsTable";
import RealestateLeadsTable from "./RealestateLeadsTable";
import { searchBusinessLeadsApi } from "./BusinessLeadsPage";
import { searchTrainingLeadsApi } from "./TrainingLeadsPage";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";

// Common source name variations (Excel spelling -> DB spelling) for import matching
const SOURCE_NAME_ALIASES = {
  justdail: ["justdial", "just dial"],
  justdial: ["justdail", "just dial"],
};

const DIGIX_LEADS_FILTERS_STORAGE_KEY = "digixLeadsFiltersState_v1";
const DIGIX_LEADS_ROWS_PER_PAGE_KEY = "digixLeadsRowsPerPage";
const DIGIX_EMPTY_FILTERS = {
  name: "",
  phone: "",
  nameOrPhone: "",
  city: "",
  domain: "",
  service: "All Services",
  status: "All Statuses",
  createdFrom: "",
  createdTo: "",
  nextFollowupDate: "",
  courseId: "",
  mode: "",
  batch: "",
  sourceId: "",
  sourceName: "",
  clientId: "",
  propertyService: "",
  isOpportunity: "",
  automationEnabled: "",
  datePreset: "all",
  freeSearch: "",
};

function readPersistedDigixFilters() {
  try {
    const raw = sessionStorage.getItem(DIGIX_LEADS_FILTERS_STORAGE_KEY)
      ?? localStorage.getItem(DIGIX_LEADS_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function digixDisplayDateSortKey(display) {
  if (!display || display === "—") return "";
  const datePart = String(display).split(" ")[0] || "";
  if (datePart.includes("-") && datePart.length >= 10) return datePart.substring(0, 10);
  const [d, m, y] = datePart.split("/");
  if (y && m && d) return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return String(display).toLowerCase();
}

function digixCreatedDateToISO(createdAtDisplay) {
  if (!createdAtDisplay || createdAtDisplay === "—") return "";
  const datePart = String(createdAtDisplay).split(" ")[0] || "";
  if (datePart.includes("-") && datePart.length >= 10) return datePart.substring(0, 10);
  const [d, m, y] = datePart.split("/");
  if (y && m && d) {
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  return "";
}

function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDatePresetRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = toISODateLocal(today);
  if (preset === "today") return { from: to, to };
  if (preset === "last7") {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { from: toISODateLocal(from), to };
  }
  if (preset === "last30") {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return { from: toISODateLocal(from), to };
  }
  if (preset === "thisMonth") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toISODateLocal(from), to };
  }
  return { from: "", to: "" };
}

function digixTrainingCoursesSortValue(row, courses) {
  const tl = row?.training_lead || row?.trainingLead || row;
  const coursesArr = Array.isArray(row?.Courses)
    ? row.Courses
    : Array.isArray(tl?.Courses)
      ? tl.Courses
      : [];
  if (coursesArr.length > 0) {
    return coursesArr
      .map((c) => `${c.Course_Code || c.course_code || ""} ${c.Course_Name || c.course_name || ""}`.trim())
      .join(" ")
      .toLowerCase();
  }
  const direct = row.courseCode || row.courseName || row.course || row.services;
  if (direct && direct !== "—") return String(direct).toLowerCase();
  if (row.courseId && Array.isArray(courses)) {
    const course = courses.find(
      (c) => String(c.Course_Id) === String(row.courseId)
    );
    if (course) {
      return `${course.Course_Code || ""} ${course.Course_Name || ""}`.trim().toLowerCase();
    }
  }
  return "";
}

function digixServicesSortValue(row) {
  if (!row.services || row.services === "—") return "";
  const arr = Array.isArray(row.services)
    ? row.services
    : String(row.services)
        .split(", ")
        .map((s) => s.trim())
        .filter(Boolean);
  return arr.join(" ").toLowerCase();
}

function digixTrainingModeSortValue(row) {
  const tl = row?.training_lead || row?.trainingLead || row;
  const modesArr = Array.isArray(row?.Modes) ? row.Modes : Array.isArray(tl?.Modes) ? tl.Modes : [];
  if (Array.isArray(row.mode)) return row.mode.join(", ").toLowerCase();
  const fromRow = row.mode || row.Mode || row.mode_name || row.modeName;
  if (fromRow) return String(fromRow).toLowerCase();
  return modesArr.map((m) => m.Mode_Name || m.mode_name).filter(Boolean).join(" ").toLowerCase();
}

function digixBusinessDomainSortValue(row, domains) {
  let domainName = row.domain && row.domain !== "—" ? row.domain : "";
  if ((!domainName || domainName === "—") && row.domainId && Array.isArray(domains) && domains.length > 0) {
    const dom = domains.find((d) => d.id === row.domainId || String(d.id) === String(row.domainId));
    if (dom?.name) domainName = dom.name;
  }
  return String(domainName || "").toLowerCase();
}

function digixNormalizeFilterValue(value) {
  return String(value || "").trim().toLowerCase();
}

function digixArrayOrCsvValues(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v || "").trim()).filter(Boolean);
  }
  return String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function getDigixLeadSortValue(lead, columnId, activeTab, courses, domains) {
  const row = lead;
  switch (columnId) {
    case "fullName":
      return String(row.name || "").toLowerCase();
    case "customerName":
      return String(row.customerName || "").toLowerCase();
    case "contact":
      return String(row.phone || "").toLowerCase();
    case "location":
      return String(row.location || "").toLowerCase();
    case "propertyLocation":
      return String(row.propertyLocation || "").toLowerCase();
    case "servicesCol":
      if (activeTab === "Training Leads") return digixTrainingCoursesSortValue(row, courses);
      if (activeTab === "Realestate Leads") return String(row.requirementType || "").toLowerCase();
      return digixServicesSortValue(row);
    case "propertyType":
      return String(row.propertyType || "").toLowerCase();
    case "propertyService":
      return String(row.servicePropertyName || "").toLowerCase();
    case "mode":
      return digixTrainingModeSortValue(row);
    case "business":
      return digixBusinessDomainSortValue(row, domains);
    case "status":
      return String(row.status || "").toLowerCase();
    case "quoteSent":
      return String(row.isQuoteSent || "").toLowerCase();
    case "lastFollowupDate":
      return digixDisplayDateSortKey(row.lastFollowupDate);
    case "notes":
      return String(row.notes || "").toLowerCase();
    case "nextFollowupDate":
      return digixDisplayDateSortKey(row.nextFollowupDate);
    case "createdAt":
      return digixDisplayDateSortKey(row.createdAt);
    case "createdBy":
      return String(row.createdBy || "").toLowerCase();
    case "source":
      return String(row.source || "").toLowerCase();
    case "companyName":
      return String(row.companyName || "").toLowerCase();
    case "email":
      return String(row.email || "").toLowerCase();
    default:
      return "";
  }
}

function DigixLeads({
  leads = [],
  realestateLeads = [],
  trainingLeads = [],
  jobLeads = [],
  internshipLeads = [],
  courses = [],
  onAddLeadClick,
  onViewLead,
  onEditLead,
  onDeleteTrainingLead,
  onRefreshRealestateLeads,
  initialActiveTab = "Business Leads",
  initialLeadType,
  onTabChange,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = (() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canAdd = canPerformActionOnPage(PAGES.DIGIX_LEADS, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.DIGIX_LEADS, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.DIGIX_LEADS, ACTIONS.DELETE, role);
  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path === "/realestate-leads" || path.startsWith("/realestate-leads/")) return "Realestate Leads";
    if (path === "/training-leads" || path.startsWith("/training-leads/")) return "Training Leads";
    if (path === "/job-leads" || path.startsWith("/job-leads/")) return "Job Leads";
    if (path === "/internship-leads" || path.startsWith("/internship-leads/")) return "Internship Leads";
    return initialActiveTab || "Business Leads";
  });
  
  // Derive leadType from activeTab
  const leadType =
    activeTab === "Realestate Leads"
      ? "Realestate Leads"
      : activeTab === "Training Leads"
        ? "Training Leads"
        : activeTab === "Job Leads"
          ? "Job Leads"
          : activeTab === "Internship Leads"
            ? "Internship Leads"
            : "Business Leads";
  
  useEffect(() => {
    if (initialActiveTab) setActiveTab(initialActiveTab);
  }, [initialActiveTab]);

  useEffect(() => {
    if (initialLeadType === "Training Leads" || location.state?.leadType === "Training Leads") {
      setActiveTab("Training Leads");
    }
  }, [initialLeadType, location.state?.leadType]);

  useEffect(() => {
    const path = location.pathname;
    if (path === "/realestate-leads" || path.startsWith("/realestate-leads/")) setActiveTab("Realestate Leads");
    else if (path === "/training-leads" || path.startsWith("/training-leads/")) setActiveTab("Training Leads");
    else if (path === "/job-leads" || path.startsWith("/job-leads/")) setActiveTab("Job Leads");
    else if (path === "/internship-leads" || path.startsWith("/internship-leads/")) setActiveTab("Internship Leads");
    else if (path === "/" || path === "/business-leads" || path.startsWith("/business-leads/")) setActiveTab("Business Leads");
  }, [location.pathname]);
  const [selectedService, setSelectedService] = useState("All Services");
  const [selectedStatus, setSelectedStatus] = useState("All Statuses");
  const [currentPage, setCurrentPage] = useState(() => {
    const path = location.pathname;
    const tabKey =
      path === "/realestate-leads" || path.startsWith("/realestate-leads/")
        ? "realestate"
        : path === "/training-leads" || path.startsWith("/training-leads/")
          ? "training"
          : "business";
    const saved = localStorage.getItem(`digixLeadsCurrentPage_${tabKey}`);
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        return parsed > 0 ? parsed : 1;
      } catch (e) {
        return 1;
      }
    }
    return 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(DIGIX_LEADS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [services, setServices] = useState(["All Services"]);
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('digixLeadsColumnWidths');
    let parsed = { business: {}, training: {} };
    if (saved) {
      try {
        const parsedSaved = JSON.parse(saved);
        parsed.business = parsedSaved.business || {};
        parsed.training = parsedSaved.training || {};
      } catch (e) {
        console.error("DigixLeads column widths parse error:", e?.message || e);
      }
    }
    return parsed;
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeRef = useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const fileInputRef = useRef(null);
  const [servicesData, setServicesData] = useState([]); // Store full service objects with IDs
  const [importing, setImporting] = useState(false);
  const [selectedReLeadIds, setSelectedReLeadIds] = useState(() => new Set());
  const persistedFilters = useMemo(() => readPersistedDigixFilters(), []);
  const [filters, setFilters] = useState(() => ({
    ...DIGIX_EMPTY_FILTERS,
    ...(persistedFilters?.filters || {}),
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    ...DIGIX_EMPTY_FILTERS,
    ...(persistedFilters?.appliedFilters || {}),
  }));
  const [sources, setSources] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [allCities, setAllCities] = useState([]); // Store all cities for city_id mapping
  const [loadingCities, setLoadingCities] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // Store API search results for training leads
  const [businessLeadsSearchResults, setBusinessLeadsSearchResults] = useState(null); // Store API search results for business leads
  const [jobLeadsSearchResults, setJobLeadsSearchResults] = useState(null); // Store API search results for job leads
  const [internshipLeadsSearchResults, setInternshipLeadsSearchResults] = useState(null); // Reserved for internship API search
  const [realestateLeadsSearchResults, setRealestateLeadsSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchRequestSeqRef = useRef(0);
  const headerFilterRef = useRef(null); // table container for click-outside
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const locationDropdownRef = useRef(null);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const serviceDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");
  const statusDropdownRef = useRef(null);
  const [isCallScriptOpen, setIsCallScriptOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target)) {
        setLocationDropdownOpen(false);
        setLocationSearchQuery("");
      }
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) {
        setDomainDropdownOpen(false);
        setDomainSearchQuery("");
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target)) {
        setServiceDropdownOpen(false);
        setServiceSearchQuery("");
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
        setStatusSearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    if (tab === "Realestate Leads") {
      navigate("/realestate-leads", { replace: true });
    } else if (tab === "Training Leads") {
      navigate("/training-leads", { replace: true });
    } else if (tab === "Job Leads") {
      navigate("/job-leads", { replace: true });
    } else if (tab === "Internship Leads") {
      navigate("/internship-leads", { replace: true });
    } else {
      navigate("/business-leads", { replace: true });
    }
    if (onTabChange) onTabChange(tab);
  };

  useEffect(() => {
    setSortColumn(null);
    setSortDirection("asc");
  }, [activeTab]);

  // Clear search results when leads/trainingLeads are refreshed (e.g. after add/edit)
  // so the table shows the updated data instead of stale cached search results
  useEffect(() => {
    setBusinessLeadsSearchResults(null);
  }, [leads]);
  useEffect(() => {
    setSearchResults(null);
  }, [trainingLeads]);
  useEffect(() => {
    setJobLeadsSearchResults(null);
  }, [jobLeads]);

  useEffect(() => {
    setInternshipLeadsSearchResults(null);
  }, [internshipLeads]);

  useEffect(() => {
    setRealestateLeadsSearchResults(null);
  }, [realestateLeads]);

  // Sync selectedService and selectedStatus with filters
  useEffect(() => {
    if (filters.service) {
      setSelectedService(filters.service);
    } else {
      setSelectedService("All Services");
    }
  }, [filters.service]);

  useEffect(() => {
    if (filters.status) {
      setSelectedStatus(filters.status);
    } else {
      setSelectedStatus("All Statuses");
    }
  }, [filters.status]);

  const searchTrainingLeads = async (seq) => {
    setIsSearching(true);
    try {
      const results = await searchTrainingLeadsApi({
        nameOrPhone: filters.nameOrPhone,
        phone: filters.phone,
        city: filters.city,
        courseId: filters.courseId,
        status: filters.status,
        nextFollowupDate: filters.nextFollowupDate,
        mode: filters.mode,
        batch: filters.batch,
        sourceId: filters.sourceId,
        freeSearch: filters.freeSearch,
      });
      if (seq === searchRequestSeqRef.current) {
        setSearchResults(results);
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || "Error searching training leads.";
      console.error("Error searching training leads:", msg);
      if (seq === searchRequestSeqRef.current) {
        setSearchResults([]);
        alert("Error searching training leads: " + (error.response?.data?.error || error.message));
      }
    } finally {
      if (seq === searchRequestSeqRef.current) {
        setIsSearching(false);
      }
    }
  };

  const searchBusinessLeads = async (seq) => {
    setIsSearching(true);
    try {
      const results = await searchBusinessLeadsApi(filters, servicesData, allCities);
      if (seq === searchRequestSeqRef.current) {
        setBusinessLeadsSearchResults(results);
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || "Error searching business leads.";
      console.error("Error searching business leads:", msg);
      if (seq === searchRequestSeqRef.current) {
        setBusinessLeadsSearchResults([]);
        alert("Error searching business leads: " + (error.response?.data?.error || error.message));
      }
    } finally {
      if (seq === searchRequestSeqRef.current) {
        setIsSearching(false);
      }
    }
  };

  // Get the current data source based on active tab
  const currentData =
    activeTab === "Realestate Leads"
      ? (realestateLeadsSearchResults || realestateLeads)
      : activeTab === "Training Leads"
        ? (searchResults || trainingLeads)
        : activeTab === "Internship Leads"
          ? (internshipLeadsSearchResults || internshipLeads)
          : activeTab === "Job Leads"
            ? (jobLeadsSearchResults || jobLeads)
            : (businessLeadsSearchResults || leads);

  const realestateCustomers = useMemo(() => {
    const byId = new Map();
    (realestateLeads || []).forEach((lead) => {
      if (lead.clientId == null) return;
      const id = String(lead.clientId);
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: String(lead.customerName || `Customer #${id}`).trim(),
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [realestateLeads]);

  // Filter leads/customers based on applied filter criteria (search on button click only)
  const f = appliedFilters;
  const filteredLeads = currentData.filter(lead => {
    const exactMatch = (left, right) =>
      digixNormalizeFilterValue(left) === digixNormalizeFilterValue(right);

    const matchesCreatedDateRange = () => {
      const leadISO = digixCreatedDateToISO(lead.createdAt);
      if (f.createdFrom) {
        if (!leadISO || leadISO < f.createdFrom) return false;
      }
      if (f.createdTo) {
        if (!leadISO || leadISO > f.createdTo) return false;
      }
      return true;
    };

    // Job / Internship / Realestate Leads: apply core filters only (client-side)
    if (activeTab === "Job Leads" || activeTab === "Internship Leads" || activeTab === "Realestate Leads") {
      if (f.name && !lead.name?.toLowerCase().includes(f.name.toLowerCase())) return false;
      if (f.phone && !lead.phone?.includes(f.phone)) return false;
      if (f.nameOrPhone) {
        const searchValue = f.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(f.nameOrPhone.trim());
        const emailMatch = lead.email?.toLowerCase().includes(searchValue);
        if (!nameMatch && !phoneMatch && !emailMatch) return false;
      }
      if (f.city) {
        if (activeTab === "Realestate Leads") {
          if (!String(lead.location || "").toLowerCase().includes(String(f.city).toLowerCase())) return false;
        } else if (!exactMatch(lead.location, f.city)) {
          return false;
        }
      }
      if (f.status && f.status !== "All Statuses") {
        const leadStatus = String(lead.status || "").trim();
        const filterStatus = String(f.status || "").trim();
        if (leadStatus.toLowerCase() !== filterStatus.toLowerCase()) return false;
      }
      if (activeTab === "Realestate Leads") {
        if (f.clientId && String(lead.clientId) !== String(f.clientId)) return false;
        if (
          f.propertyService &&
          !String(lead.servicePropertyName || "")
            .toLowerCase()
            .includes(String(f.propertyService).trim().toLowerCase())
        ) return false;
        if (f.sourceName && !exactMatch(lead.source, f.sourceName)) return false;
        if (f.isOpportunity !== "" && lead.isOpportunity !== (f.isOpportunity === "true")) return false;
        if (f.automationEnabled !== "" && lead.automationEnabled !== (f.automationEnabled === "true")) return false;
      }
      if (!matchesCreatedDateRange()) return false;
      return true;
    }

    // For training leads, apply local refinement even when using API results.
    // This guarantees combined filters (e.g. nameOrPhone + phone) behave as AND.
    if (activeTab === "Training Leads" && f.nameOrPhone) {
      const searchValue = String(f.nameOrPhone).toLowerCase().trim();
      const leadName = String(lead.name || "").toLowerCase();
      const leadPhone = String(lead.phone || "");
      const matchesNameOrPhone =
        leadName.includes(searchValue) || leadPhone.includes(String(f.nameOrPhone).trim());
      if (!matchesNameOrPhone) return false;
    }
    if (activeTab === "Training Leads" && f.phone) {
      const phoneValue = String(f.phone).trim();
      if (!String(lead.phone || "").includes(phoneValue)) return false;
    }

    // For training leads, apply location filter even when using API results
    if (activeTab === "Training Leads" && f.city) {
      if (!exactMatch(lead.location, f.city)) {
        return false;
      }
    }
    // For training leads, enforce exact status match even when using API results.
    // This protects against backend partial-status matches.
    if (activeTab === "Training Leads" && f.status && f.status !== "All Statuses") {
      if (!exactMatch(lead.status, f.status)) {
        return false;
      }
    }
    
    // Skip other filters for training leads when using API results
    if (activeTab === "Training Leads" && searchResults !== null) {
      if (!matchesCreatedDateRange()) return false;
      return true; // API already filtered, only apply location filter above
    }
    
    // For business leads with API results, apply name/phone, city and domain filters
    if (activeTab === "Business Leads" && businessLeadsSearchResults !== null) {
      if (f.name && !lead.name?.toLowerCase().includes(f.name.toLowerCase())) return false;
      if (f.phone && !lead.phone?.includes(f.phone)) return false;
      if (f.nameOrPhone) {
        const searchValue = f.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(f.nameOrPhone.trim());
        if (!nameMatch && !phoneMatch) return false;
      }
      // Apply city filter
      if (f.city) {
        if (!exactMatch(lead.location, f.city)) {
          return false;
        }
      }
      // Apply domain filter (using domain name or Domain_Id + domains list)
      if (f.domain) {
        const selectedDomain = digixNormalizeFilterValue(f.domain);

        const leadDomainName = digixNormalizeFilterValue(lead.domain);

        let leadDomainFromId = "";
        if (lead.domainId && Array.isArray(domains) && domains.length > 0) {
          const dom = domains.find(
            (d) => d.id === lead.domainId || String(d.id) === String(lead.domainId)
          );
          if (dom && dom.name) {
            leadDomainFromId = digixNormalizeFilterValue(dom.name);
          }
        }

        if (leadDomainName !== selectedDomain && leadDomainFromId !== selectedDomain) {
          return false;
        }
      }
      // Apply service filter as exact option match
      if (f.service && f.service !== "All Services") {
        const selectedService = digixNormalizeFilterValue(f.service);
        const leadServices = digixArrayOrCsvValues(lead.services).map(digixNormalizeFilterValue);
        if (!leadServices.includes(selectedService)) return false;
      }
      // Apply status filter as exact value match
      if (f.status && f.status !== "All Statuses") {
        if (!exactMatch(lead.status, f.status)) return false;
      }
      if (!matchesCreatedDateRange()) return false;
      return true;
    }
    
    // Combined name/phone filter for business leads (client-side when not using API results)
    if (activeTab === "Business Leads" && f.nameOrPhone) {
      const searchValue = f.nameOrPhone.toLowerCase().trim();
      const nameMatch = lead.name?.toLowerCase().includes(searchValue);
      const phoneMatch = lead.phone?.includes(f.nameOrPhone.trim());
      if (!nameMatch && !phoneMatch) {
        return false;
      }
    }
    // Name filter
    if (f.name && !lead.name?.toLowerCase().includes(f.name.toLowerCase())) {
      return false;
    }
    // Phone filter
    if (f.phone && !lead.phone?.includes(f.phone)) {
      return false;
    }
    // City filter
    if (f.city && !exactMatch(lead.location, f.city)) {
      return false;
    }
    // Domain filter (when not using API search results)
    if (f.domain) {
      const selectedDomain = digixNormalizeFilterValue(f.domain);

      const leadDomainName = digixNormalizeFilterValue(lead.domain);

      let leadDomainFromId = "";
      if (lead.domainId && Array.isArray(domains) && domains.length > 0) {
        const dom = domains.find(
          (d) => d.id === lead.domainId || String(d.id) === String(lead.domainId)
        );
        if (dom && dom.name) {
          leadDomainFromId = digixNormalizeFilterValue(dom.name);
        }
      }

      if (leadDomainName !== selectedDomain && leadDomainFromId !== selectedDomain) {
        return false;
      }
    }
    // Service filter
    if (f.service && f.service !== "All Services") {
      const selectedService = digixNormalizeFilterValue(f.service);
      const leadServices = digixArrayOrCsvValues(lead.services).map(digixNormalizeFilterValue);
      if (!leadServices.includes(selectedService)) return false;
    }
    // Status filter
    if (f.status && f.status !== "All Statuses") {
      const leadStatus = String(lead.status || "").trim();
      const filterStatus = String(f.status || "").trim();
      if (leadStatus.toLowerCase() !== filterStatus.toLowerCase()) return false;
    }
    if (!matchesCreatedDateRange()) return false;
    // Next Followup Date filter
    if (f.nextFollowupDate) {
      const filterDate = f.nextFollowupDate; // YYYY-MM-DD
      if (!lead.nextFollowupDate || lead.nextFollowupDate === "—") {
        return false;
      }
      const datePart = lead.nextFollowupDate.split(' ')[0] || "";
      let leadDateISO = "";
      if (datePart.includes("-") && datePart.length >= 10) {
        leadDateISO = datePart.substring(0, 10); // already YYYY-MM-DD
      } else {
        const [d, m, y] = datePart.split("/");
        if (y && m && d) leadDateISO = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      if (leadDateISO !== filterDate) return false;
    }
    return true;
  });

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(DIGIX_LEADS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  // Apply free search filter for all tabs (searches across all columns)
  const freeSearchFilteredLeads = f.freeSearch
    ? filteredLeads.filter(lead => {
        const searchValue = f.freeSearch.toLowerCase().trim();
        if (!searchValue) return true;
        
        // Common fields for leads
        const name = (lead.name || "").toLowerCase();
        const email = (lead.email || "").toLowerCase();
        const phone = (lead.phone || "").toLowerCase();
        const location = (lead.location || "").toLowerCase();
        const createdAt = (lead.createdAt || "").toLowerCase();
        const lastFollowupDate = (lead.lastFollowupDate || "").toLowerCase();
        const notes = (lead.notes || "").toLowerCase();
        const nextFollowupDate = (lead.nextFollowupDate || "").toLowerCase();
        const status = (lead.status || "").toLowerCase();
        const source = (lead.source || "").toLowerCase();
        const isQuoteSent = (lead.isQuoteSent || "").toString().toLowerCase();
        
        // Business Leads and Customers specific fields
        const companyName = (lead.companyName || "").toLowerCase();
        const domain = (lead.domain || "").toLowerCase();
        const services = Array.isArray(lead.services) 
          ? lead.services.join(", ").toLowerCase() 
          : (lead.services || "").toLowerCase();
        
        // Training Leads specific - get course name
        let courseName = "";
        if (activeTab === "Training Leads" && lead.courseId) {
          const course = courses.find(c => c.Course_Id === lead.courseId);
          courseName = (course?.Course_Name || "").toLowerCase();
        }
        
        // Base search across common fields
        let matches = name.includes(searchValue) ||
                     email.includes(searchValue) ||
                     phone.includes(searchValue) ||
                     location.includes(searchValue) ||
                     createdAt.includes(searchValue) ||
                     lastFollowupDate.includes(searchValue) ||
                     notes.includes(searchValue) ||
                     nextFollowupDate.includes(searchValue) ||
                     status.includes(searchValue) ||
                     source.includes(searchValue) ||
                     isQuoteSent.includes(searchValue);
        
        if (activeTab === "Business Leads") {
          matches = matches ||
                   companyName.includes(searchValue) ||
                   domain.includes(searchValue) ||
                   services.includes(searchValue);
        }

        if (activeTab === "Job Leads" || activeTab === "Internship Leads" || activeTab === "Realestate Leads") {
          matches = matches ||
                   companyName.includes(searchValue) ||
                   services.includes(searchValue);
        }
        
        if (activeTab === "Training Leads") {
          matches = matches || courseName.includes(searchValue);
        }
        
        return matches;
      })
    : filteredLeads;

  const sortedLeads = !sortColumn
    ? freeSearchFilteredLeads
    : [...freeSearchFilteredLeads].sort((a, b) => {
        const mult = sortDirection === "desc" ? -1 : 1;
        const va = getDigixLeadSortValue(a, sortColumn, activeTab, courses, domains);
        const vb = getDigixLeadSortValue(b, sortColumn, activeTab, courses, domains);
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return (ida - idb) * mult;
      });

  const totalPages = Math.max(1, Math.ceil(sortedLeads.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedLeads.slice(startIndex, endIndex);

  const currentLeads = pageData;
  const currentReLeadIds =
    activeTab === "Realestate Leads"
      ? currentLeads.map((lead) => String(lead.id))
      : [];
  const allCurrentReLeadsSelected =
    currentReLeadIds.length > 0 &&
    currentReLeadIds.every((id) => selectedReLeadIds.has(id));

  const handleToggleReLeadSelection = (leadId) => {
    const id = String(leadId);
    setSelectedReLeadIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAllCurrentReLeads = () => {
    setSelectedReLeadIds((previous) => {
      const next = new Set(previous);
      if (allCurrentReLeadsSelected) {
        currentReLeadIds.forEach((id) => next.delete(id));
      } else {
        currentReLeadIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Fetch services/categories from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axiosInstance.get('/service/categories');
        if (response.data && Array.isArray(response.data)) {
          // Store full data structure for ID mapping
          setServicesData(response.data);
          // Flatten subcategory names from all categories and prepend "All Services"
          const subcategoryNames = response.data.flatMap(category =>
            (category.subcategories || []).map(sub => sub.subcategory_name)
          );
          setServices(["All Services", ...subcategoryNames.sort()]);
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.message || error.code || "Error fetching services.";
        console.error("Error fetching services:", msg, error);
      }
    };

    fetchServices();
  }, []);

  // Fetch sources for training leads, business leads, and realestate leads filter
  useEffect(() => {
    if (activeTab === "Training Leads" || activeTab === "Business Leads" || activeTab === "Realestate Leads") {
      const fetchSources = async () => {
        setLoadingSources(true);
        try {
          const response = await axiosInstance.get('/lead-sources');
          const raw = response?.data;
          const sourcesArr = Array.isArray(raw) ? raw : (raw?.sources || raw?.data?.sources || raw?.data || raw?.lead_sources || []);
          setSources(Array.isArray(sourcesArr) ? sourcesArr : []);
        } catch (error) {
          console.error("Error fetching sources:", error.response?.data?.message || error.message || error);
          setSources([]);
        } finally {
          setLoadingSources(false);
        }
      };
      fetchSources();
    }
  }, [activeTab]);

  // Fetch all cities from master table for location filter (Business Leads, Training Leads, Customers)
  useEffect(() => {
    const fetchAllCities = async () => {
      setLoadingCities(true);
      try {
        const response = await axiosInstance.get('/cities');
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        setAllCities(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error("Error fetching cities for filters:", error);
        setAllCities([]);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchAllCities();
  }, []);


  // Save current page to localStorage whenever it changes (tab-specific)
  useEffect(() => {
    if (currentPage > 0) {
      const tabKey = activeTab === "Training Leads" ? "training" : activeTab === "Realestate Leads" ? "realestate" : "business";
      localStorage.setItem(`digixLeadsCurrentPage_${tabKey}`, currentPage.toString());
    }
  }, [currentPage, activeTab]);

  // Restore page when switching tabs
  useEffect(() => {
    const tabKey = activeTab === "Training Leads" ? "training" : activeTab === "Realestate Leads" ? "realestate" : "business";
    const saved = localStorage.getItem(`digixLeadsCurrentPage_${tabKey}`);
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        if (parsed > 0) {
          setCurrentPage(parsed);
        }
      } catch (e) {
        // Ignore parse errors
      }
    } else {
      setCurrentPage(1);
    }
  }, [activeTab]);

  useEffect(() => {
    // Reset to page 1 if current page is beyond total pages
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [freeSearchFilteredLeads.length, currentPage, totalPages]);

  // Format date from YYYY-MM-DD to dd/mm/yyyy for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format date from dd/mm/yyyy to YYYY-MM-DD for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  const [leadStatuses, setLeadStatuses] = useState([]);

  // Lead statuses for filters (from /lead-statuses API)
  useEffect(() => {
    const fetchLeadStatuses = async () => {
      try {
        const response = await axiosInstance.get("/lead-statuses");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? ""
          }))
          .filter((s) => s.id != null && s.name.trim() !== "");
        setLeadStatuses(mapped);
      } catch (err) {
        console.error("Error fetching lead statuses for filters:", err);
        setLeadStatuses([]);
      }
    };
    fetchLeadStatuses();
  }, []);

  const statuses = useMemo(
    () => ["All Statuses", ...leadStatuses.map((s) => s.name).sort()],
    [leadStatuses]
  );

  // Unique locations for dropdown, preferring master city list from /cities
  const allLocations = useMemo(() => {
    // Primary: use master cities list
    if (allCities && allCities.length > 0) {
      const namesSet = new Set(
        allCities
          .map(c => c.city_name || c.City_Name)
          .filter(Boolean)
          .map(s => String(s).trim())
      );
      const names = Array.from(namesSet).sort();
      return ["All Locations", ...names];
    }

    // Fallback: derive from current data if master list unavailable
    const data =
      activeTab === "Realestate Leads"
        ? realestateLeads
        : activeTab === "Training Leads"
          ? trainingLeads
          : activeTab === "Internship Leads"
            ? internshipLeads
            : activeTab === "Job Leads"
              ? jobLeads
              : leads;
    const locs = new Set(
      (data || [])
        .map(l => l.location || l.Location)
        .filter(Boolean)
        .map(s => String(s).trim())
    );
    return ["All Locations", ...Array.from(locs).sort()];
  }, [activeTab, leads, realestateLeads, trainingLeads, jobLeads, internshipLeads, allCities]);

  // Domains for filters (from /domains API)
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get("/domains");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains for filters:", err);
      }
    };
    fetchDomains();
  }, []);

  const allDomains = useMemo(
    () => ["All businesses", ...domains.map((d) => d.name).sort()],
    [domains]
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      setAppliedFilters(next); // search on type (no Search button)
      return next;
    });
    setCurrentPage(1);
  };

  const hasSearchFilters = () => {
    if (activeTab === "Training Leads") {
      // Training leads can be fully handled by the search API
      return (
        filters.nameOrPhone ||
        filters.phone ||
        filters.courseId ||
        filters.city ||
        (filters.status && filters.status !== "All Statuses")
      );
    }
    // For business leads, domain is handled purely on the client side,
    // so it is intentionally excluded from the API filter set.
    return (
      filters.name ||
      filters.phone ||
      filters.city ||
      (filters.service && filters.service !== "All Services") ||
      (filters.status && filters.status !== "All Statuses")
    );
  };

  // Search runs while typing (debounced) — Search button removed.
  useEffect(() => {
    const seq = ++searchRequestSeqRef.current;
    const t = setTimeout(() => {
      // No API-backed search for these tabs.
      if (activeTab === "Job Leads" || activeTab === "Internship Leads" || activeTab === "Realestate Leads") {
        setJobLeadsSearchResults(null);
        setInternshipLeadsSearchResults(null);
        setRealestateLeadsSearchResults(null);
        if (seq === searchRequestSeqRef.current) {
          setIsSearching(false);
        }
        return;
      }

      if (activeTab === "Training Leads") {
        if (hasSearchFilters()) {
          searchTrainingLeads(seq);
        } else {
          setSearchResults(null);
          if (seq === searchRequestSeqRef.current) {
            setIsSearching(false);
          }
        }
        return;
      }

      // Business Leads
      if (hasSearchFilters()) {
        searchBusinessLeads(seq);
      } else {
        setBusinessLeadsSearchResults(null);
        if (seq === searchRequestSeqRef.current) {
          setIsSearching(false);
        }
      }
    }, 350);

    return () => clearTimeout(t);
  }, [filters, activeTab]);

  // Backward-compatible handler for any lingering JSX binding.
  // Search now runs on type via the debounced effect above.
  const handleSearchClick = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const emptyFilters = DIGIX_EMPTY_FILTERS;

  // Dashboard DigiX table: open tab with status pre-selected (location.state.digixFilter)
  useEffect(() => {
    const df = location.state?.digixFilter;
    if (df == null || typeof df !== "object") return;

    const raw = df.status;
    const statusVal =
      raw === undefined || raw === null || raw === "" || raw === "All Statuses"
        ? "All Statuses"
        : String(raw);

    const next = { ...emptyFilters, status: statusVal };
    setFilters(next);
    setAppliedFilters(next);
    setSelectedService("All Services");
    setSelectedStatus(statusVal);
    setBusinessLeadsSearchResults(null);
    setSearchResults(null);
    setJobLeadsSearchResults(null);
    setInternshipLeadsSearchResults(null);
    setRealestateLeadsSearchResults(null);
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection("asc");
    setStatusDropdownOpen(false);
    setStatusSearchQuery("");

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state?.digixFilter]);

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedService("All Services");
    setSelectedStatus("All Statuses");
    setSearchResults(null);
    setBusinessLeadsSearchResults(null);
    setJobLeadsSearchResults(null);
    setInternshipLeadsSearchResults(null);
    setRealestateLeadsSearchResults(null);
    setLocationDropdownOpen(false);
    setDomainDropdownOpen(false);
    setServiceDropdownOpen(false);
    setStatusDropdownOpen(false);
    setLocationSearchQuery("");
    setDomainSearchQuery("");
    setServiceSearchQuery("");
    setStatusSearchQuery("");
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection("asc");
    try {
      sessionStorage.removeItem(DIGIX_LEADS_FILTERS_STORAGE_KEY);
      localStorage.removeItem(DIGIX_LEADS_FILTERS_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  };

  useEffect(() => {
    try {
      const payload = JSON.stringify({
        filters,
        appliedFilters,
        activeTab,
      });
      sessionStorage.setItem(DIGIX_LEADS_FILTERS_STORAGE_KEY, payload);
      localStorage.setItem(DIGIX_LEADS_FILTERS_STORAGE_KEY, payload);
    } catch {
      // ignore storage errors
    }
  }, [filters, appliedFilters, activeTab]);

  // Column resize handlers
  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnIndex);
    const th = e.currentTarget.parentElement;
    const startWidth = th.offsetWidth;
    const startX = e.clientX;
    
    resizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    // Add resizing class to body
    document.body.classList.add('resizing');

    const handleMouseMove = (e) => {
      if (resizeRef.current.columnIndex === null) return;
      
      const diff = e.clientX - resizeRef.current.startX;
      const newWidth = Math.max(50, resizeRef.current.startWidth + diff);
      const tabKey = getTabKey();
      
      setColumnWidths(prev => {
        const newWidths = { ...prev };
        if (!newWidths[tabKey]) {
          newWidths[tabKey] = {};
        }
        newWidths[tabKey][resizeRef.current.columnIndex] = newWidth;
        return newWidths;
      });
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove('resizing');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Save to localStorage
      setColumnWidths(current => {
        localStorage.setItem('digixLeadsColumnWidths', JSON.stringify(current));
        return current;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Default column widths
  const defaultColumnWidths = {
    // Business Leads / Customers (15 columns)
    0: 120,   // Full Name
    1: 150,   // Email
    2: 120,   // Contact
    3: 150,   // Company Name
    4: 120,   // Business (domain catalog)
    5: 120,   // Location
    6: 150,   // Services
    7: 110,   // Created On
    8: 140,   // Latest Follow-up Date
    9: 180,   // Latest Follow-up Notes
    10: 140,  // Next Follow-up Date
    11: 100,  // Status
    12: 100,  // Source
    13: 100,  // Quote Sent
    14: 120,  // Action
    // Training Leads (13 columns) - different indices
    // 0-2 same as above
    // 3: Location
    // 4: Course
    // 5: Created On
    // etc.
  };

  const getTabKey = () => (activeTab === "Training Leads" ? "training" : activeTab === "Realestate Leads" ? "realestate" : "business");

  // Get column width or default
  const getColumnWidth = (columnIndex) => {
    const tabKey = getTabKey();
    if (columnWidths[tabKey] && columnWidths[tabKey][columnIndex]) {
      return `${columnWidths[tabKey][columnIndex]}px`;
    }
    return undefined; // Let CSS handle default widths
  };

  // Get column min-width style
  const getColumnMinWidth = (columnIndex) => {
    const tabKey = getTabKey();
    if (columnWidths[tabKey] && columnWidths[tabKey][columnIndex]) {
      return `${columnWidths[tabKey][columnIndex]}px`;
    }
    return undefined;
  };

  const handleView = (id) => {
    if (!onViewLead) return;
    const type =
      activeTab === "Realestate Leads"
        ? "realestate-lead"
        : activeTab === "Training Leads"
          ? "training-lead"
          : activeTab === "Job Leads"
            ? "job-lead"
            : activeTab === "Internship Leads"
              ? "internship-lead"
              : "lead";
    onViewLead(id, type);
  };

  const handleToggleReFlag = async (leadId, field, value) => {
    if (!canEdit || !leadId) return;
    const apiFields = {
      siteVisit: "Site_Visit",
      isOpportunity: "Is_Opportunity",
      automationEnabled: "Automation_Enabled",
    };
    if (!apiFields[field]) return;
    try {
      await axiosInstance.put(`/update-real-estate-lead/${leadId}`, {
        [apiFields[field]]: !!value,
      });
      if (typeof onRefreshRealestateLeads === "function") {
        await onRefreshRealestateLeads();
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to update real estate lead.";
      alert(msg);
    }
  };

  const handleAddReFollowupNote = async (row, remarks) => {
    if (!canEdit || !row?.id) return;
    const value = remarks && String(remarks).trim() !== "" ? String(remarks).trim() : null;
    if (!value) return;
    let userId = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = user.user_id || user.id || 1;
      }
    } catch {
      /* ignore */
    }
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const localNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    try {
      await axiosInstance.post(`/create-real-estate-followup`, {
        Real_Estate_Lead_Id: row.id,
        Followup_Date: localNow,
        Remarks: value,
        Created_By: userId,
        Updated_By: userId,
      });
      if (typeof onRefreshRealestateLeads === "function") {
        await onRefreshRealestateLeads();
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to add followup.";
      alert(msg);
    }
  };

  const handleUpdateReStatus = async (leadId, statusId) => {
    if (!canEdit || !leadId || !statusId) return;
    try {
      await axiosInstance.put(`/update-real-estate-lead/${leadId}`, {
        Lead_Status_Id: Number(statusId),
      });
      if (typeof onRefreshRealestateLeads === "function") {
        await onRefreshRealestateLeads();
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to update real estate lead status.";
      alert(msg);
    }
  };

  const applyDatePreset = (preset) => {
    if (preset === "custom") {
      setFilters((prev) => {
        const next = { ...prev, datePreset: "custom" };
        setAppliedFilters(next);
        return next;
      });
      return;
    }
    const range = getDatePresetRange(preset);
    setFilters((prev) => {
      const next = {
        ...prev,
        datePreset: preset,
        createdFrom: range.from,
        createdTo: range.to,
      };
      setAppliedFilters(next);
      return next;
    });
  };

  const handleEdit = (id) => {
    if (canEdit && onEditLead) {
      const type =
        activeTab === "Realestate Leads"
          ? "realestate-lead"
          : activeTab === "Training Leads"
            ? "training-lead"
            : activeTab === "Job Leads"
              ? "job-lead"
              : activeTab === "Internship Leads"
                ? "internship-lead"
                : "lead";
      onEditLead(id, type);
    }
  };

  const handleDelete = (id) => {
    if (canDelete && activeTab === "Training Leads" && onDeleteTrainingLead) {
      onDeleteTrainingLead(id);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleExportRealestateLeads = () => {
    if (activeTab !== "Realestate Leads") return;
    const selectedLeads = realestateLeads.filter((lead) =>
      selectedReLeadIds.has(String(lead.id))
    );
    if (!selectedLeads.length) {
      alert("Please select at least one real estate lead to export.");
      return;
    }

    const exportRows = selectedLeads.map((lead) => ({
      "Customer Name": lead.customerName === "—" ? "" : lead.customerName || "",
      "Property / Service": lead.servicePropertyName === "—" ? "" : lead.servicePropertyName || "",
      "Property / Service Link": lead.propertyServiceLink || "",
      "First Name": lead.firstName || "",
      "Last Name": lead.lastName || "",
      "Country Code": lead.countryCode || "",
      "Mobile Number": lead.phone === "—" ? "" : lead.phone || "",
      Email: lead.email === "—" ? "" : lead.email || "",
      "Property Type": lead.propertyType === "—" ? "" : lead.propertyType || "",
      "Requirement Type": lead.requirementType === "—" ? "" : lead.requirementType || "",
      "Budget Min": lead.budgetMin ?? "",
      "Budget Max": lead.budgetMax ?? "",
      "Lead Location": lead.location === "—" ? "" : lead.location || "",
      "Property Location": lead.propertyLocation === "—" ? "" : lead.propertyLocation || "",
      "Site Visit": lead.siteVisit ? "Yes" : "No",
      Notes: lead.notes === "—" ? "" : lead.notes || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Real Estate Leads");
    XLSX.writeFile(
      workbook,
      `real-estate-leads-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      alert('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
      return;
    }

    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) {
        alert('Excel file must contain at least a header row and one data row');
        setImporting(false);
        return;
      }

      // Get headers (first row)
      const headers = jsonData[0].map(h => String(h || '').trim().toLowerCase());
      
      // Process data rows
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''));
      
      if (rows.length === 0) {
        alert('No data rows found in Excel file');
        setImporting(false);
        return;
      }

      // Get logged-in user id
      let createdBy = 1;
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          createdBy = user.user_id || user.id || 1;
        }
      } catch (error) {
        console.error("Error reading user data:", error);
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // For Training Leads, fetch sources and courses fresh so lookups work (Excel: Sources_id, Cources_id)
      let importSources = sources;
      let importCourses = courses;
      let importPropertyTypes = [];
      let importStatuses = leadStatuses;
      let importCustomers = [];
      if (activeTab === "Training Leads") {
        try {
          const [sourcesRes, coursesRes] = await Promise.all([
            axiosInstance.get('/lead-sources'),
            axiosInstance.get('/courses')
          ]);
          const rawSrc = sourcesRes?.data;
          importSources = Array.isArray(rawSrc) ? rawSrc : (rawSrc?.sources || rawSrc?.data || rawSrc?.data?.sources || rawSrc?.data?.lead_sources || rawSrc?.lead_sources || []);
          if (!Array.isArray(importSources)) importSources = [];
          const rawCrs = coursesRes?.data;
          importCourses = Array.isArray(rawCrs) ? rawCrs : (rawCrs?.courses || rawCrs?.data || rawCrs?.data?.courses || []);
          if (!Array.isArray(importCourses)) importCourses = [];
        } catch (fetchErr) {
          console.error("Error fetching sources/courses for import:", fetchErr);
        }
      }
      if (activeTab === "Realestate Leads") {
        try {
          const [propertyTypesRes, sourcesRes, statusesRes, customersRes] = await Promise.all([
            axiosInstance.get("/property-types"),
            axiosInstance.get("/lead-sources"),
            axiosInstance.get("/lead-statuses"),
            axiosInstance.get("/clients"),
          ]);
          const unwrapArray = (response, keys = []) => {
            const raw = response?.data;
            if (Array.isArray(raw)) return raw;
            for (const key of keys) {
              if (Array.isArray(raw?.[key])) return raw[key];
            }
            return Array.isArray(raw?.data) ? raw.data : [];
          };
          importPropertyTypes = unwrapArray(propertyTypesRes, ["property_types", "results"]);
          importSources = unwrapArray(sourcesRes, ["sources", "lead_sources", "results"]);
          importStatuses = unwrapArray(statusesRes, ["statuses", "lead_statuses", "results"]);
          // Clients endpoint returns [{ client: {...}, address: {...} }, ...]
          const customersRaw = unwrapArray(customersRes, ["clients", "customers", "results"]);
          importCustomers = customersRaw.map((entry) => {
            const c = entry?.client || entry || {};
            const fullName = `${c.First_Name || ""} ${c.Last_Name || ""}`.trim();
            return {
              id: c.client_id ?? c.client_id ?? null,
              name: fullName,
              company: String(c.Company_Name || "").trim(),
            };
          }).filter((c) => c.id != null);
        } catch (fetchErr) {
          console.error("Error fetching real estate lead import lookups:", fetchErr);
          alert("Could not load real estate lead lookup data. Please try again.");
          return;
        }
      }

      // Process each row and send to API
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowObj = {};
        headers.forEach((header, colIndex) => {
          // Normalize header to lower-case key and convert cell value to trimmed string
          const key = String(header || "").trim().toLowerCase();
          const cellValue = row[colIndex];
          rowObj[key] =
            cellValue === null || cellValue === undefined
              ? ""
              : String(cellValue).trim();
        });

        try {
          if (activeTab === "Business Leads") {
            // Parse name - handle various column name formats
            const firstName = rowObj['first name'] || rowObj['firstname'] || rowObj['first_name'] || '';
            const lastName = rowObj['last name'] || rowObj['lastname'] || rowObj['last_name'] || '';
            const fullName = rowObj['name'] || '';
            let parsedFirstName = firstName;
            let parsedLastName = lastName;
            
            if (!firstName && !lastName && fullName) {
              const nameParts = fullName.trim().split(' ');
              parsedFirstName = nameParts[0] || '';
              parsedLastName = nameParts.slice(1).join(' ') || '';
            }

            // Parse phone - handle "Phone Number" / "Personal Phone Number" / Phone / Contact
            const phoneRaw =
              rowObj['personal phone number'] ||
              rowObj['phone number'] ||
              rowObj['phone'] ||
              rowObj['contact'] ||
              rowObj['personal phone'] ||
              '';
            const phone = phoneRaw ? String(phoneRaw).trim() : '';

            // Parse email - handle "Personal Email" column
            const emailRaw = rowObj['personal email'] || rowObj['email'] || null;
            const email = emailRaw ? String(emailRaw).trim() : null;

            // Parse services (comma-separated service names/IDs)
            const servicesStr = rowObj['services'] || '';
            const serviceIds = [];
            if (servicesStr && servicesData.length > 0) {
              const serviceNames = servicesStr.split(',').map(s => s.trim());
              for (const serviceName of serviceNames) {
                for (const category of servicesData) {
                  if (category.subcategories && Array.isArray(category.subcategories)) {
                    const subcategory = category.subcategories.find(
                      sub => sub.subcategory_name?.toLowerCase() === serviceName.toLowerCase()
                    );
                    if (subcategory) {
                      serviceIds.push(subcategory.subcategory_id);
                      break;
                    }
                  }
                }
              }
            }

            // Find source ID from source name
            const sourceName = rowObj['source'] || rowObj['source name'] || '';
            let sourceId = null;
            if (sourceName && sources.length > 0) {
              const searchLower = sourceName.toLowerCase().trim();

              const sourceAliases = {
                'justdail': ['justdial', 'just dial'],
                'justdial': ['justdail', 'just dial'],
                'just dial': ['justdail', 'justdial']
              };

              const variants = [searchLower, ...(sourceAliases[searchLower] || [])];

              let source = sources.find(s => {
                const sName = (s.source_name || s.Source_Name || s.source_Name || '').toString().toLowerCase();
                return variants.includes(sName) || sName === searchLower;
              });

              // Fallback: prefix match for minor typos
              if (!source && searchLower.length >= 5) {
                const prefix = searchLower.substring(0, 5);
                source = sources.find(s => {
                  const sName = (s.source_name || s.Source_Name || s.source_Name || '').toString().toLowerCase();
                  return sName.length >= 5 && (sName.startsWith(prefix) || prefix.startsWith(sName.substring(0, 5)));
                });
              }

              sourceId = source ? (source.source_id ?? source.Source_Id ?? source.source_Id) : null;
            }
            if (!sourceId && rowObj['source id'] && !isNaN(parseInt(rowObj['source id'], 10))) {
              sourceId = parseInt(rowObj['source id'], 10);
            }

            // Map domain name to Domain_Id using loaded domains (if available)
            let domainId = null;
            const domainName = rowObj['domain'] || '';
            if (domainName && domains && domains.length > 0) {
              const dom = domains.find(
                (d) => (d.name || '').toLowerCase() === domainName.toLowerCase()
              );
              if (dom) domainId = dom.id;
            }

            // Map status name to Status_Id using loaded leadStatuses (if available)
            let statusId = null;
            const statusName = rowObj['status'] || '';
            if (statusName && leadStatuses && leadStatuses.length > 0) {
              const st = leadStatuses.find(
                (s) => (s.name || '').toLowerCase() === statusName.toLowerCase()
              );
              if (st) statusId = st.id;
            }

            // Validate required fields (First_Name, Phone, Source_Id) before hitting API
            if (!parsedFirstName || !phone || !sourceId) {
              errors.push(`Row ${i + 2}: First_Name, Phone and Source_Id are required`);
              errorCount++;
              continue;
            }

            const payload = {
              First_Name: parsedFirstName,
              Last_Name: parsedLastName || null,
              Email: email,
              Phone: phone,
              Alt_Phone: rowObj['alt phone'] || rowObj['altphone'] || null,
              Source_Id: sourceId,
              Status_Id: statusId,
              Domain_Id: domainId,
              Created_By: createdBy,
              Lead_Type: "Business",
              services: serviceIds,
              Company_Name: rowObj['company name'] || rowObj['companyname'] || rowObj['company'] || null,
              Company_Email: rowObj['company email'] || rowObj['companyemail'] || null,
              Company_Phone: rowObj['company phone'] || rowObj['companyphone'] || null,
              Website: rowObj['website'] || null,
              Address_Line_1: rowObj['address'] || rowObj['address line 1'] || rowObj['addressline1'] || null,
              Address_Line_2: rowObj['address line 2'] || rowObj['addressline2'] || null,
              City_Id: null, // Can be enhanced to lookup city by name
              State_Id: null,
              Country_Id: null,
              Zip_Code: rowObj['zip code'] || rowObj['zipcode'] || null,
              Com_Lang: rowObj['communication language'] || rowObj['comlang'] || null,
              Is_Quote_Sent: (rowObj['quote sent'] || '').toLowerCase() === 'yes' ? 1 : 0
            };

            await axiosInstance.post('/create-leads', payload);
            successCount++;

          } else if (activeTab === "Training Leads") {
            // Parse name - handle various column name formats (First name, Last name, etc.)
            const firstName = rowObj['first name'] || rowObj['firstname'] || rowObj['first_name'] || '';
            const lastName = rowObj['last name'] || rowObj['lastname'] || rowObj['last_name'] || '';
            const fullName = rowObj['name'] || '';
            let parsedFirstName = firstName;
            let parsedLastName = lastName;
            
            if (!firstName && !lastName && fullName) {
              const nameParts = fullName.trim().split(' ');
              parsedFirstName = nameParts[0] || '';
              parsedLastName = nameParts.slice(1).join(' ') || '';
            }

            // Parse phone - handle Contact, Phone, Personal Phone Number (Excel: "Contact")
            const phoneRaw = rowObj['contact'] || rowObj['personal phone number'] || rowObj['phone'] || rowObj['personal phone'] || '';
            const phone = phoneRaw ? String(phoneRaw).trim() : '';

            // Parse email - handle "Personal Email" or "Email" column
            const emailRaw = rowObj['email'] || rowObj['personal email'] || null;
            const email = emailRaw ? String(emailRaw).trim() : null;

            // Find course ID - handle Cources_id (typo), Course_Id, course name/code (Excel: "Cources_id" with value "web development")
            const courseNameOrCode = (rowObj['cources_id'] || rowObj['courses_id'] || rowObj['course_id'] || rowObj['course name'] || rowObj['coursename'] || rowObj['course code'] || rowObj['coursecode'] || '').trim();
            let courseId = rowObj['course id'] || rowObj['courseid'] || '';
            if (!courseId && courseNameOrCode && importCourses.length > 0) {
              const course = importCourses.find(c => {
                const name = (c.Course_Name || c.course_name || '').toString().toLowerCase();
                const code = (c.Course_Code || c.course_code || '').toString().toLowerCase();
                const search = courseNameOrCode.toLowerCase();
                return name === search || code === search;
              });
              courseId = course ? (course.Course_Id ?? course.course_id) : null;
            }
            if (courseId && typeof courseId !== 'number') courseId = isNaN(parseInt(courseId, 10)) ? null : parseInt(courseId, 10);

            // Find source ID from source name - handle Sources_id (typo), Source_Id (Excel: "Sources_id" with value "Justdail")
            const sourceName = (rowObj['sources_id'] || rowObj['source_id'] || rowObj['source id'] || rowObj['source'] || '').trim();
            let sourceId = null;
            if (sourceName && importSources.length > 0) {
              const searchLower = sourceName.toLowerCase();
              // Common name variations (Excel typo vs DB spelling, e.g. Justdail <-> JustDial)
              const sourceAliases = {
                'justdail': ['justdial', 'just dial'],
                'justdial': ['justdail', 'just dial'],
                'just dial': ['justdail', 'justdial']
              };
              const variants = [searchLower, ...(sourceAliases[searchLower] || [])];
              let source = importSources.find(s => {
                const sName = (s.source_name || s.Source_Name || s.source_Name || '').toString().toLowerCase();
                return variants.includes(sName) || sName === searchLower;
              });
              // Fallback: match by first 5 chars (handles "justdail" vs "justdial" typo)
              if (!source && searchLower.length >= 5) {
                const prefix = searchLower.substring(0, 5);
                source = importSources.find(s => {
                  const sName = (s.source_name || s.Source_Name || s.source_Name || '').toString().toLowerCase();
                  return sName.length >= 5 && (sName.startsWith(prefix) || prefix.startsWith(sName.substring(0, 5)));
                });
              }
              sourceId = source ? (source.source_id ?? source.Source_Id ?? source.source_Id) : null;
            }
            if (!sourceId && (rowObj['sources_id'] || rowObj['source_id'] || rowObj['source id']) && !isNaN(parseInt(rowObj['sources_id'] || rowObj['source_id'] || rowObj['source id'], 10))) {
              sourceId = parseInt(rowObj['sources_id'] || rowObj['source_id'] || rowObj['source id'], 10);
            }

            // Validate required fields (backend requires First_Name, Course_Id, Source_Id)
            if (!parsedFirstName && !parsedLastName) {
              errors.push(`Row ${i + 2}: First_Name, Course_Id and Source_Id are required`);
              errorCount++;
              continue;
            }
            if (!parsedFirstName) {
              errors.push(`Row ${i + 2}: First_Name, Course_Id and Source_Id are required`);
              errorCount++;
              continue;
            }
            if (!courseId) {
              errors.push(`Row ${i + 2}: First_Name, Course_Id and Source_Id are required (Course: "${courseNameOrCode || 'empty'}" not found - check Cources_id/Course column matches a valid course)`);
              errorCount++;
              continue;
            }
            if (!sourceId) {
              const availableSources = importSources.length > 0
                ? importSources.slice(0, 5).map(s => (s.source_name || s.Source_Name || '').trim()).filter(Boolean).join(', ')
                : '(none loaded)';
              errors.push(`Row ${i + 2}: Source "${sourceName || 'empty'}" not found. Available: ${availableSources}${importSources.length > 5 ? '...' : ''}. Add the source in Settings or fix Excel to match exactly.`);
              errorCount++;
              continue;
            }
            if (!phone || phone.trim() === '') {
              errors.push(`Row ${i + 2}: Missing required field (Contact/Phone)`);
              errorCount++;
              continue;
            }

            const payload = {
              First_Name: parsedFirstName || null,
              Last_Name: parsedLastName || null,
              Phone: phone.trim(),
              Alt_Phone: (rowObj['alt phone'] || rowObj['altphone']) ? String(rowObj['alt phone'] || rowObj['altphone']).trim() : null,
              Email: email || null,
              Qualification_Id: rowObj['qualification id'] && !isNaN(rowObj['qualification id']) ? parseInt(rowObj['qualification id']) : null,
              Qualification_Name: (rowObj['qualification name'] || '').trim() || null,
              College_Name: (rowObj['college name'] || rowObj['collegename'] || '').trim() || null,
              Year_Of_Passing: (rowObj['year of passing'] || rowObj['yearofpassing'] || '').trim() || null,
              Expected_Fee: rowObj['expected fee'] || rowObj['expectedfee'] ? (isNaN(parseFloat(rowObj['expected fee'] || rowObj['expectedfee'])) ? null : parseFloat(rowObj['expected fee'] || rowObj['expectedfee'])) : null,
              Earliest_Start_Date: (rowObj['earliest start date'] || rowObj['earlieststartdate'] || '').trim() || null,
              Batch: (rowObj['batch'] || '').trim() || null,
              Mode: (rowObj['mode'] || '').trim() || null,
              Course_Id: courseId && !isNaN(courseId) ? parseInt(courseId) : null,
              Source_Id: sourceId ? parseInt(sourceId) : (rowObj['source id'] && !isNaN(rowObj['source id']) ? parseInt(rowObj['source id']) : null),
              Address_Line_1: (rowObj['address'] || rowObj['address line 1'] || rowObj['addressline1'] || rowObj['loaction'] || rowObj['location'] || '').trim() || null,
              Address_Line_2: (rowObj['address line 2'] || rowObj['addressline2'] || '').trim() || null,
              City_Id: null,
              State_Id: null,
              Country_Id: null
            };

            try {
              await axiosInstance.post('/create-training-lead', payload);
              successCount++;
            } catch (apiError) {
              const errorDetails = apiError.response?.data;
              const errMsg = errorDetails?.message || errorDetails?.error || apiError.message || "Unknown error";
              console.error(`API Error for row ${i + 2}:`, errMsg);
              
              let errorMsg = `Row ${i + 2}: `;
              
              if (apiError.response?.status === 400) {
                if (errorDetails?.error) {
                  errorMsg += errorDetails.error;
                } else if (errorDetails?.message) {
                  errorMsg += errorDetails.message;
                } else if (errorDetails?.errors) {
                  if (Array.isArray(errorDetails.errors)) {
                    errorMsg += errorDetails.errors.join(', ');
                  } else if (typeof errorDetails.errors === 'object') {
                    const errorList = Object.entries(errorDetails.errors).map(([key, val]) => `${key}: ${val}`).join(', ');
                    errorMsg += errorList || JSON.stringify(errorDetails.errors);
                  } else {
                    errorMsg += String(errorDetails.errors);
                  }
                } else {
                  errorMsg += `Bad Request (400). Check console for details.`;
                }
              } else {
                errorMsg += errorDetails?.message || errorDetails?.error || apiError.message || 'Unknown error';
              }
              
              errors.push(errorMsg);
              errorCount++;
            }

          } else if (activeTab === "Realestate Leads") {
            const firstName =
              rowObj["first name"] || rowObj.firstname || rowObj.first_name || "";
            const lastName =
              rowObj["last name"] || rowObj.lastname || rowObj.last_name || "";
            const mobileNumber =
              rowObj["mobile number"] ||
              rowObj.mobile ||
              rowObj.phone ||
              rowObj.contact ||
              "";
            // Resolve the customer: prefer Customer ID; if not provided, match by Customer Name.
            const customerIdValue = String(
              rowObj["customer id"] || rowObj.client_id || rowObj["client id"] || ""
            ).trim();
            const customerNameValue = String(
              rowObj["customer name"] || rowObj.customer_name || ""
            ).trim();

            let resolvedCustomerId = null;
            if (customerIdValue) {
              const parsedId = Number.parseInt(customerIdValue, 10);
              if (!Number.isNaN(parsedId) && importCustomers.some((c) => Number(c.id) === parsedId)) {
                resolvedCustomerId = parsedId;
              } else {
                errors.push(`Row ${i + 2}: Invalid Client ID "${customerIdValue}"`);
                errorCount++;
                continue;
              }
            } else if (customerNameValue) {
              const normalizedName = customerNameValue.toLowerCase();
              const matched = importCustomers.find(
                (c) =>
                  c.name.toLowerCase() === normalizedName ||
                  (c.company && c.company.toLowerCase() === normalizedName)
              );
              if (matched) {
                resolvedCustomerId = Number(matched.id);
              } else {
                errors.push(`Row ${i + 2}: Customer Name "${customerNameValue}" not found`);
                errorCount++;
                continue;
              }
            }

            if (!firstName || !mobileNumber || resolvedCustomerId == null) {
              errors.push(
                `Row ${i + 2}: First Name, Mobile Number, and a valid Customer ID or Customer Name are required`
              );
              errorCount++;
              continue;
            }
            const clientId = resolvedCustomerId;

            const findLookupId = (items, value, idKeys, nameKeys) => {
              const text = String(value || "").trim();
              if (!text) return null;
              const numericId = Number.parseInt(text, 10);
              if (!Number.isNaN(numericId)) {
                const byId = items.find((item) =>
                  idKeys.some((key) => Number(item?.[key]) === numericId)
                );
                if (byId) {
                  return idKeys.map((key) => byId?.[key]).find((id) => id != null) ?? null;
                }
              }
              const normalized = text.toLowerCase();
              const byName = items.find((item) =>
                nameKeys.some(
                  (key) => String(item?.[key] ?? "").trim().toLowerCase() === normalized
                )
              );
              return byName
                ? idKeys.map((key) => byName?.[key]).find((id) => id != null) ?? null
                : null;
            };

            const propertyTypeValue =
              rowObj["property type"] || rowObj["property type id"] || rowObj.property_type_id;
            const sourceValue =
              rowObj.source || rowObj["source name"] || rowObj["source id"] || rowObj.source_id;
            const statusValue =
              rowObj.status || rowObj["status name"] || rowObj["status id"] || rowObj.status_id;
            const propertyTypeId = findLookupId(
              importPropertyTypes,
              propertyTypeValue,
              ["Property_Type_Id", "property_type_id", "id"],
              ["Property_Type_Name", "property_type_name", "name"]
            );
            const sourceId = findLookupId(
              importSources,
              sourceValue,
              ["Source_Id", "source_id", "id"],
              ["Source_Name", "source_name", "name"]
            );
            const statusId = findLookupId(
              importStatuses,
              statusValue,
              ["Status_Id", "status_id", "id"],
              ["Status", "status", "name"]
            );
            if (!sourceId || !statusId) {
              errors.push(
                `Row ${i + 2}: Valid Source and Status values are required`
              );
              errorCount++;
              continue;
            }
            const parseMoney = (value) => {
              if (value === null || value === undefined || String(value).trim() === "") return null;
              const parsed = Number(String(value).replace(/,/g, ""));
              return Number.isFinite(parsed) ? parsed : null;
            };
            const siteVisitValue = String(
              rowObj["site visit"] || rowObj.site_visit || ""
            ).trim().toLowerCase();
            const opportunityValue = String(
              rowObj.opportunity || rowObj["is opportunity"] || rowObj.is_opportunity || ""
            ).trim().toLowerCase();
            const automationValue = String(
              rowObj.automation || rowObj["automation enabled"] || rowObj.automation_enabled || ""
            ).trim().toLowerCase();

            await axiosInstance.post("/create-real-estate-lead", {
              client_id: clientId,
              Property_Service:
                rowObj["property / service"] ||
                rowObj["property/service"] ||
                rowObj["property service"] ||
                rowObj.property_service ||
                null,
              Property_Service_Link:
                rowObj["property / service link"] ||
                rowObj["property/service link"] ||
                rowObj["property service link"] ||
                rowObj.property_service_link ||
                null,
              First_Name: firstName,
              Last_Name: lastName || null,
              Country_Code: rowObj["country code"] || rowObj.country_code || "+91",
              Mobile_Number: String(mobileNumber).trim(),
              Alt_Phone: rowObj["alt phone"] || rowObj.alt_phone || null,
              Email: rowObj.email || null,
              Property_Type_Id: propertyTypeId,
              Requirement_Type:
                rowObj["requirement type"] || rowObj.requirement_type || null,
              Budget_Min: parseMoney(rowObj["budget min"] || rowObj.budget_min),
              Budget_Max: parseMoney(rowObj["budget max"] || rowObj.budget_max),
              Preferred_Location:
                rowObj["lead location"] ||
                rowObj["preferred location"] ||
                rowObj.location ||
                null,
              Property_Location:
                rowObj["property location"] || rowObj.property_location || null,
              Lead_Status_Id: statusId,
              Source_Id: sourceId,
              Site_Visit: ["yes", "true", "1"].includes(siteVisitValue),
              Is_Opportunity: ["yes", "true", "1"].includes(opportunityValue),
              Automation_Enabled: ["yes", "true", "1"].includes(automationValue),
              Notes: rowObj.notes || null,
              Created_By: createdBy,
            });
            successCount++;
          }
        } catch (error) {
          // This catch block handles non-API errors (like validation errors before API call)
          console.error(`Error importing row ${i + 2}:`, error);
          const errorDetails = error.response?.data;
          let errorMsg = `Row ${i + 2}: `;
          if (errorDetails?.error) {
            errorMsg += errorDetails.error;
          } else if (errorDetails?.message) {
            errorMsg += errorDetails.message;
          } else if (errorDetails?.errors && Array.isArray(errorDetails.errors)) {
            errorMsg += errorDetails.errors.join(', ');
          } else if (errorDetails?.errors && typeof errorDetails.errors === 'object') {
            errorMsg += JSON.stringify(errorDetails.errors);
          } else {
            errorMsg += error.message || 'Unknown error';
          }
          errors.push(errorMsg);
          errorCount++;
        }
      }

      // Show results
      let message = `Import completed: ${successCount} ${activeTab.toLowerCase()} imported successfully`;
      if (errorCount > 0) {
        message += `, ${errorCount} failed`;
        if (errors.length > 0) {
          message += '\n\nErrors:\n' + errors.slice(0, 5).join('\n');
          if (errors.length > 5) {
            message += `\n... and ${errors.length - 5} more errors`;
          }
        }
      }
      alert(message);

      // Refresh the page data by reloading
      window.location.reload();

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error importing Excel file:', error);
      alert('Error importing Excel file. Please check the file format and try again.');
    } finally {
      setImporting(false);
    }
  };

  const callScriptData = [
    {
      speaker: "Agent",
      text: "We got an inquiry regarding website or digital marketing, right?"
    },
    {
      speaker: "Customer",
      text: "If they ask from where you got?",
      response: "Agent: Just Dial."
    },
    {
      speaker: "Customer",
      text: "Yes",
      response: "Agent: Great, thanks for confirming. Continue."
    },
    {
      speaker: "Customer",
      text: "No",
      response:
        "Agent: Oh sorry, maybe someone from your team submitted it. Just wanted to quickly check - are you currently looking for website or lead generation services?"
    },
    {
      speaker: "Customer",
      text: "If Yes",
      response: "Continue."
    },
    {
      speaker: "Customer",
      text: "If No",
      response:
        "Agent: No problem. I'll keep it brief - if you need help in future, we can assist with leads and websites. End politely."
    },
    {
      speaker: "Agent",
      text: "Is this a good time for a quick 2-minute discussion?"
    },
    {
      speaker: "Customer",
      text: "Yes",
      response: "Continue."
    },
    {
      speaker: "Customer",
      text: "No",
      response: "Agent: No problem. When would be a better time to call you?"
    },
    {
      speaker: "Customer",
      text: "Capture time",
      response: "Agent: Perfect, I'll call you then. End."
    },
    {
      speaker: "Agent",
      text: "Can you tell me a bit about your business and what you're looking for?"
    },
    {
      speaker: "Customer",
      text: "Explains requirement",
      response: "Agent: Acknowledge. Continue."
    },
    {
      speaker: "Customer",
      text: "Just exploring",
      response:
        "Agent: Got it - are you planning to start soon or just checking options for now? Continue based on answer."
    },
    {
      speaker: "Customer",
      text: "Already working with someone",
      response:
        "Agent: That's great. Are you satisfied with the results or exploring better options?"
    },
    {
      speaker: "Customer",
      text: "Satisfied",
      response:
        "Agent: Good to hear. If you ever want to compare or scale, we can help. Soft exit or continue lightly."
    },
    {
      speaker: "Customer",
      text: "Not satisfied",
      response: "Continue (opportunity)."
    },
    {
      speaker: "Agent",
      text:
        "So we help businesses like yours generate leads through Google Ads, SEO, and social media - along with building high-converting websites."
    },
    {
      speaker: "Customer",
      text: "Interested",
      response: "Continue."
    },
    {
      speaker: "Customer",
      text: "Not interested",
      response: "Agent: Understood. Just one quick thing - are you currently getting enough leads from online?"
    },
    {
      speaker: "Customer",
      text: "If No",
      response: "Continue (re-open)."
    },
    {
      speaker: "Customer",
      text: "If Yes",
      response: "Agent: That's great. If you plan to scale further, we can support. Soft exit."
    },
    {
      speaker: "Agent",
      text:
        "We recently worked with similar businesses and helped them increase their leads significantly within a few weeks."
    },
    {
      speaker: "Customer",
      text: "How?",
      response:
        "Agent: We analyze your business, target audience, and competitors, then run optimized campaigns to bring quality leads."
    },
    {
      speaker: "Customer",
      text: "Price?",
      response:
        "Agent: It depends on your requirement and goals. I can suggest the best plan after a quick understanding of your needs."
    },
    {
      speaker: "Customer",
      text: "Send details",
      response: "Agent: Sure, I'll share a brief plan tailored to your business. Move to WhatsApp."
    },
    {
      speaker: "Agent",
      text: "Should I share details on WhatsApp or schedule a quick 15-minute call?"
    },
    {
      speaker: "Customer",
      text: "WhatsApp",
      response:
        "Agent: Sure, I'll send you details right away. This is your WhatsApp number, right? Confirm and send."
    },
    {
      speaker: "Customer",
      text: "Call",
      response:
        "Agent: Great, what time works best for you today or tomorrow? Book slot and confirm calendar."
    },
    {
      speaker: "Customer",
      text: "Not interested",
      response:
        "Agent: No worries at all. If you need help in future for website or leads, you can reach us anytime. Have a great day! End."
    }
  ];

  return (
    <div className="digixleads-container">
      <div className="main-card">
        <div className="page-header">
          <div className="page-header-row">
          <div className="tabs-container">
            <button
              className={`tab-button ${activeTab === "Business Leads" ? "active" : ""}`}
              onClick={() => handleTabChange("Business Leads")}
            >
              Business Leads
            </button>
            <button
              className={`tab-button ${activeTab === "Realestate Leads" ? "active" : ""}`}
              onClick={() => handleTabChange("Realestate Leads")}
            >
              Realestate Leads
            </button>
            <button
              className={`tab-button ${activeTab === "Training Leads" ? "active" : ""}`}
              onClick={() => handleTabChange("Training Leads")}
            >
              Training Leads
            </button>
            <button
              className={`tab-button ${activeTab === "Job Leads" ? "active" : ""}`}
              onClick={() => handleTabChange("Job Leads")}
            >
              Job Leads
            </button>
            <button
              className={`tab-button ${activeTab === "Internship Leads" ? "active" : ""}`}
              onClick={() => handleTabChange("Internship Leads")}
            >
              Internship Leads
            </button>
          </div>
          <div className="page-header-script-action">
            <button
              type="button"
              className="call-script-btn"
              onClick={() => setIsCallScriptOpen(true)}
            >
              Agent
            </button>
          </div>
          </div>
        </div>

        <div className={`search-actions-row ${activeTab === "Realestate Leads" ? "re-filter-panel" : ""}`}>
          <div className={`search-filters-row search-filters-row--customers-dates ${activeTab === "Realestate Leads" ? "re-filter-grid" : ""}`}>
                {activeTab === "Realestate Leads" && (
                  <div className="re-date-presets">
                    {[
                      { id: "all", label: "All time" },
                      { id: "today", label: "Today" },
                      { id: "last7", label: "Last 7 days" },
                      { id: "last30", label: "Last 30 days" },
                      { id: "thisMonth", label: "This month" },
                      { id: "custom", label: "Custom" },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`re-date-preset ${(filters.datePreset || "all") === p.id ? "active" : ""}`}
                        onClick={() => applyDatePreset(p.id)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
                <input
                  type="text"
                  className="search-filter-input"
                  placeholder={
                    activeTab === "Realestate Leads"
                      ? "Search leads..."
                      : activeTab === "Training Leads"
                      ? "Search name, phone, email..."
                      : "Search name..."
                  }
                  value={
                    activeTab === "Realestate Leads"
                      ? (filters.freeSearch || "")
                      : activeTab === "Training Leads"
                      ? (filters.nameOrPhone || "")
                      : (filters.name || "")
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      activeTab === "Realestate Leads"
                        ? "freeSearch"
                        : activeTab === "Training Leads"
                        ? "nameOrPhone"
                        : "name",
                      e.target.value
                    )
                  }
                />
                {activeTab === "Realestate Leads" && (
                  <input
                    type="text"
                    className="search-filter-input"
                    placeholder="Property / Service"
                    value={filters.propertyService || ""}
                    onChange={(e) => handleFilterChange("propertyService", e.target.value)}
                    title="Property / Service"
                  />
                )}
                {activeTab !== "Realestate Leads" && (
                <input
                  type="text"
                  className="search-filter-input"
                  placeholder="Search phone..."
                  value={filters.phone}
                  onChange={(e) => handleFilterChange("phone", e.target.value)}
                />
                )}
                {/* Location - searchable dropdown */}
                <div className="domain-filter-dropdown" ref={locationDropdownRef}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" onClick={() => setLocationDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLocationDropdownOpen((o) => !o); } }}>
                    <span>{filters.city || "All Locations"}</span>
                    <FaChevronDown className={`domain-filter-chevron ${locationDropdownOpen ? "open" : ""}`} />
                  </div>
                  {locationDropdownOpen && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search locations..." value={locationSearchQuery} onChange={(e) => setLocationSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!filters.city ? "selected" : ""}`} onClick={() => { handleFilterChange("city", ""); setLocationDropdownOpen(false); setLocationSearchQuery(""); }}>All Locations</div>
                        {(() => {
                          const locsWithoutAll = allLocations.filter((l) => l !== "All Locations");
                          const filtered = locsWithoutAll.filter((loc) => !locationSearchQuery.trim() || String(loc).toLowerCase().includes(locationSearchQuery.trim().toLowerCase()));
                          return filtered.length > 0 ? filtered.map((loc) => (
                            <div key={loc} className={`domain-filter-option ${filters.city === loc ? "selected" : ""}`} onClick={() => { handleFilterChange("city", loc); setLocationDropdownOpen(false); setLocationSearchQuery(""); }}>{loc}</div>
                          )) : <div className="domain-filter-no-results">No locations found</div>;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                {/* Service/Course - searchable dropdown (hidden for Job/Internship Leads) */}
                {activeTab !== "Job Leads" && activeTab !== "Internship Leads" && activeTab !== "Realestate Leads" && (
                <div className="domain-filter-dropdown" ref={serviceDropdownRef}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" onClick={() => setServiceDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setServiceDropdownOpen((o) => !o); } }}>
                    <span>
                      {activeTab === "Training Leads"
                        ? (filters.courseId ? (courses?.find((c) => String(c.Course_Id) === String(filters.courseId)) ? `${courses.find((c) => String(c.Course_Id) === String(filters.courseId)).Course_Code} - ${courses.find((c) => String(c.Course_Id) === String(filters.courseId)).Course_Name}` : "All Courses")
                        : "All Courses")
                        : (filters.service === "All Services" || !filters.service ? "All Services" : filters.service)}
                    </span>
                    <FaChevronDown className={`domain-filter-chevron ${serviceDropdownOpen ? "open" : ""}`} />
                  </div>
                  {serviceDropdownOpen && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder={activeTab === "Training Leads" ? "Search courses..." : "Search services..."} value={serviceSearchQuery} onChange={(e) => setServiceSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${(activeTab === "Training Leads" ? !filters.courseId : (!filters.service || filters.service === "All Services")) ? "selected" : ""}`} onClick={() => { handleFilterChange(activeTab === "Training Leads" ? "courseId" : "service", activeTab === "Training Leads" ? "" : "All Services"); setServiceDropdownOpen(false); setServiceSearchQuery(""); }}>{activeTab === "Training Leads" ? "All Courses" : "All Services"}</div>
                        {activeTab === "Training Leads" ? (
                          (() => {
                            const activeCourses = (courses || []).filter((c) => c.Status === "Active");
                            const displayCourses = activeCourses.map((c) => ({ id: c.Course_Id, label: `${c.Course_Code || ""} - ${c.Course_Name || ""}`.trim() }));
                            const filtered = displayCourses.filter((c) => !serviceSearchQuery.trim() || c.label.toLowerCase().includes(serviceSearchQuery.trim().toLowerCase()));
                            return filtered.length > 0 ? filtered.map((c) => (
                              <div key={c.id} className={`domain-filter-option ${String(filters.courseId) === String(c.id) ? "selected" : ""}`} onClick={() => { handleFilterChange("courseId", String(c.id)); setServiceDropdownOpen(false); setServiceSearchQuery(""); }}>{c.label}</div>
                            )) : <div className="domain-filter-no-results">No courses found</div>;
                          })()
                        ) : (
                          (() => {
                            const svcsWithoutAll = services.filter((s) => s !== "All Services");
                            const filtered = svcsWithoutAll.filter((s) => !serviceSearchQuery.trim() || String(s).toLowerCase().includes(serviceSearchQuery.trim().toLowerCase()));
                            return filtered.length > 0 ? filtered.map((s) => (
                              <div key={s} className={`domain-filter-option ${filters.service === s ? "selected" : ""}`} onClick={() => { handleFilterChange("service", s); setServiceDropdownOpen(false); setServiceSearchQuery(""); }}>{s}</div>
                            )) : <div className="domain-filter-no-results">No services found</div>;
                          })()
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}
                {activeTab === "Realestate Leads" && (
                  <>
                    <select
                      className="search-filter-input search-filter-select"
                      value={filters.clientId || ""}
                      onChange={(e) => handleFilterChange("clientId", e.target.value)}
                      title="Client"
                    >
                      <option value="">All Clients</option>
                      {realestateCustomers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                    <select
                      className="search-filter-input search-filter-select"
                      value={filters.sourceName || ""}
                      onChange={(e) => handleFilterChange("sourceName", e.target.value)}
                      title="Source"
                    >
                      <option value="">All Sources</option>
                      {(sources || []).map((s) => {
                        const name = s.Source_Name || s.source_name || s.name || "";
                        const id = s.Source_Id ?? s.source_id ?? s.id;
                        return name ? (
                          <option key={id ?? name} value={name}>{name}</option>
                        ) : null;
                      })}
                    </select>
                  </>
                )}
                {activeTab === "Business Leads" && (
                  <div className="domain-filter-dropdown" ref={domainDropdownRef}>
                    <div className="search-filter-input search-filter-select domain-filter-trigger" onClick={() => setDomainDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                      <span>{filters.domain || "All businesses"}</span>
                      <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                    </div>
                    {domainDropdownOpen && (
                      <div className="domain-filter-panel">
                        <input type="text" className="domain-filter-search" placeholder="Search businesses..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                        <div className="domain-filter-options">
                          <div className={`domain-filter-option ${!filters.domain ? "selected" : ""}`} onClick={() => { handleFilterChange("domain", ""); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>All businesses</div>
                          {(() => {
                            const domsWithoutAll = allDomains.filter((d) => d !== "All businesses");
                            const filtered = domsWithoutAll.filter((d) => !domainSearchQuery.trim() || String(d).toLowerCase().includes(domainSearchQuery.trim().toLowerCase()));
                            return filtered.length > 0 ? filtered.map((dom) => (
                              <div key={dom} className={`domain-filter-option ${filters.domain === dom ? "selected" : ""}`} onClick={() => { handleFilterChange("domain", dom); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{dom}</div>
                            )) : <div className="domain-filter-no-results">No businesses found</div>;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Status - searchable dropdown */}
                <div className="domain-filter-dropdown" ref={statusDropdownRef}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" onClick={() => setStatusDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStatusDropdownOpen((o) => !o); } }}>
                    <span>{filters.status || "All Statuses"}</span>
                    <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                  </div>
                  {statusDropdownOpen && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search statuses..." value={statusSearchQuery} onChange={(e) => setStatusSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!filters.status || filters.status === "All Statuses" ? "selected" : ""}`} onClick={() => { handleFilterChange("status", ""); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>All Statuses</div>
                        {(() => {
                          const stsWithoutAll = statuses.filter((s) => s !== "All Statuses");
                          const filtered = stsWithoutAll.filter((s) => !statusSearchQuery.trim() || String(s).toLowerCase().includes(statusSearchQuery.trim().toLowerCase()));
                          return filtered.length > 0 ? filtered.map((s) => (
                            <div key={s} className={`domain-filter-option ${filters.status === s ? "selected" : ""}`} onClick={() => { handleFilterChange("status", s); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>{s}</div>
                          )) : <div className="domain-filter-no-results">No statuses found</div>;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                <label className="customers-date-filter-field">
                  <span className="customers-date-filter-field__label">Created from</span>
                  <input
                    type="date"
                    className="search-filter-input"
                    title="Created from"
                    value={filters.createdFrom}
                    onChange={(e) => handleFilterChange("createdFrom", e.target.value)}
                  />
                </label>
                <label className="customers-date-filter-field">
                  <span className="customers-date-filter-field__label">Created to</span>
                  <input
                    type="date"
                    className="search-filter-input"
                    title="Created to"
                    value={filters.createdTo}
                    onChange={(e) => handleFilterChange("createdTo", e.target.value)}
                  />
                </label>
                <button type="button" className="btn-clear" onClick={handleClearFilters}>Clear</button>
            </div>
          <div className="header-actions">
            {canAdd && (
              <button className="btn-primary btn-add-lead" onClick={() => onAddLeadClick(activeTab, leadType)}>
                {activeTab === "Realestate Leads"
                  ? "New Lead"
                  : activeTab === "Training Leads"
                    ? "New Training Lead"
                    : activeTab === "Job Leads"
                      ? "New Job Lead"
                      : activeTab === "Internship Leads"
                        ? "New Internship Lead"
                        : "New Lead"}
              </button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
            />
            {canAdd && activeTab !== "Job Leads" && activeTab !== "Internship Leads" && (
              <span
                role="button"
                tabIndex={0}
                className={`link-import-leads ${importing ? "disabled" : ""}`}
                onClick={importing ? undefined : handleImportClick}
                onKeyDown={(e) => { if (!importing && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleImportClick(); } }}
              >
                {importing ? 'Importing...' : 'Import'}
              </span>
            )}
            {activeTab === "Realestate Leads" && (
              <span
                role="button"
                tabIndex={0}
                className="link-import-leads"
                onClick={handleExportRealestateLeads}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleExportRealestateLeads();
                  }
                }}
              >
                Export{selectedReLeadIds.size > 0 ? ` (${selectedReLeadIds.size})` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="digixleads-content">
        {activeTab !== "Realestate Leads" && (
        <div className="search-row-2">
          <div className="free-search-bar search-in-actions-row">
            <input
              type="text"
              className="free-search-input"
              placeholder="Search..."
              value={filters.freeSearch}
              onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
              title="Search across all columns"
            />
          </div>
        </div>
        )}
        <div className="table-wrapper" ref={headerFilterRef}>
        <div className="table-container">
          <table className={`data-table leads-table-frozen ${activeTab === "Realestate Leads" ? "re-leads-table" : ""}`}>
              <thead>
                <tr>
                  <th className="col-sticky col-0 digix-th-sortable" style={{ minWidth: getColumnMinWidth(0) || (activeTab === "Realestate Leads" ? "130px" : "90px"), width: getColumnWidth(0), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      {activeTab === "Realestate Leads" && (
                        <input
                          type="checkbox"
                          checked={allCurrentReLeadsSelected}
                          onChange={handleToggleAllCurrentReLeads}
                          onClick={(e) => e.stopPropagation()}
                          title="Select all leads on this page"
                          aria-label="Select all leads on this page"
                          style={{ marginRight: 8 }}
                        />
                      )}
                      <SupportSortableHeader columnId="fullName" label="Full Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, 0); }}></span>
                  </th>
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(1) || (activeTab === "Realestate Leads" ? "150px" : "80px"), width: getColumnWidth(1), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="contact" label="Contact" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 1)}></span>
                  </th>
                  {activeTab === "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(2) || "140px", width: getColumnWidth(2), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="email" label="Email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 2)}></span>
                    </th>
                  )}
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 3 : 2) || "100px", width: getColumnWidth(activeTab === "Realestate Leads" ? 3 : 2), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="location" label={activeTab === "Realestate Leads" ? "Lead Location" : "Location"} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 3 : 2)}></span>
                  </th>
                  {activeTab === "Realestate Leads" && (
                    <>
                      <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(4) || "100px", width: getColumnWidth(4), position: "relative" }}>
                        <div className="digix-th-sort-row">
                          <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 4)}></span>
                      </th>
                      <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(5) || "170px", width: getColumnWidth(5), position: "relative" }}>
                        <div className="digix-th-sort-row">
                          <SupportSortableHeader columnId="customerName" label="Customer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 5)}></span>
                      </th>
                      <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(6) || "130px", width: getColumnWidth(6), position: "relative" }}>
                        <div className="digix-th-sort-row">
                          <SupportSortableHeader columnId="propertyService" label="Property / Service" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 6)}></span>
                      </th>
                      <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(18) || "120px", width: getColumnWidth(18), position: "relative" }}>
                        <div className="digix-th-sort-row">
                          <SupportSortableHeader columnId="propertyLocation" label="Property Location" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 18)}></span>
                      </th>
                    </>
                  )}
                  {activeTab === "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(7) || "100px", width: getColumnWidth(7), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="propertyType" label="Property Type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 7)}></span>
                    </th>
                  )}
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 8 : 3) || "120px", width: getColumnWidth(activeTab === "Realestate Leads" ? 8 : 3), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader
                        columnId="servicesCol"
                        label={
                          activeTab === "Training Leads"
                            ? "Courses"
                            : activeTab === "Job Leads" || activeTab === "Internship Leads"
                              ? "Skills"
                              : activeTab === "Realestate Leads"
                                ? "Requirement Type"
                                : "Services"
                        }
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 8 : 3)}></span>
                  </th>
                  {activeTab === "Training Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(4) || "100px", width: getColumnWidth(4), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="mode" label="Modes" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 4)}></span>
                    </th>
                  )}
                  {activeTab !== "Training Leads" && activeTab !== "Job Leads" && activeTab !== "Internship Leads" && activeTab !== "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(4) || "100px", width: getColumnWidth(4), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="business" label="Business" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 4)}></span>
                    </th>
                  )}
                  {activeTab !== "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Training Leads" ? 5 : 6) || "100px", width: getColumnWidth(activeTab === "Training Leads" ? 5 : 6), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Training Leads" ? 5 : 6)}></span>
                    </th>
                  )}
                  {activeTab !== "Training Leads" && activeTab !== "Internship Leads" && activeTab !== "Job Leads" && activeTab !== "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(6) || "100px", width: getColumnWidth(6), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="quoteSent" label="Quote Sent" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 6)}></span>
                    </th>
                  )}
                  {activeTab === "Realestate Leads" && (
                    <th style={{ minWidth: "90px", width: getColumnWidth(9), position: "relative" }}>
                      Site Visit
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 9)}></span>
                    </th>
                  )}
                  {activeTab === "Realestate Leads" && (
                    <>
                      <th style={{ minWidth: "100px", width: getColumnWidth(10), position: "relative" }}>
                        Opportunity
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 10)}></span>
                      </th>
                      <th style={{ minWidth: "100px", width: getColumnWidth(11), position: "relative" }}>
                        Automation
                        <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 11)}></span>
                      </th>
                    </>
                  )}
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 12 : activeTab === "Training Leads" ? 6 : 7) || "140px", width: getColumnWidth(activeTab === "Realestate Leads" ? 12 : activeTab === "Training Leads" ? 6 : 7), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="lastFollowupDate" label="Follow-up Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 12 : activeTab === "Training Leads" ? 6 : 7)}></span>
                  </th>
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 13 : activeTab === "Training Leads" ? 7 : 8) || "150px", width: getColumnWidth(activeTab === "Realestate Leads" ? 13 : activeTab === "Training Leads" ? 7 : 8), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="notes" label="Follow-up Notes" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 13 : activeTab === "Training Leads" ? 7 : 8)}></span>
                  </th>
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 14 : activeTab === "Training Leads" ? 8 : 9) || "140px", width: getColumnWidth(activeTab === "Realestate Leads" ? 14 : activeTab === "Training Leads" ? 8 : 9), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader
                        columnId={activeTab === "Realestate Leads" ? "createdBy" : "nextFollowupDate"}
                        label={activeTab === "Realestate Leads" ? "Created By" : "Next Follow-up Date"}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 14 : activeTab === "Training Leads" ? 8 : 9)}></span>
                  </th>
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 15 : activeTab === "Training Leads" ? 9 : 10) || "100px", width: getColumnWidth(activeTab === "Realestate Leads" ? 15 : activeTab === "Training Leads" ? 9 : 10), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="createdAt" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <div className="digix-th-date-format-hint">(dd/mm/yyyy)</div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 15 : activeTab === "Training Leads" ? 9 : 10)}></span>
                  </th>
                  <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 16 : activeTab === "Training Leads" ? 10 : 11) || "100px", width: getColumnWidth(activeTab === "Realestate Leads" ? 16 : activeTab === "Training Leads" ? 10 : 11), position: "relative" }}>
                    <div className="digix-th-sort-row">
                      <SupportSortableHeader columnId="source" label="Source" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 16 : activeTab === "Training Leads" ? 10 : 11)}></span>
                  </th>
                  {activeTab !== "Training Leads" && activeTab !== "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(11) || "120px", width: getColumnWidth(11), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="companyName" label="Company Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 11)}></span>
                    </th>
                  )}
                  {activeTab !== "Realestate Leads" && (
                    <th className="digix-th-sortable" style={{ minWidth: getColumnMinWidth(activeTab === "Training Leads" ? 11 : 12) || "100px", width: getColumnWidth(activeTab === "Training Leads" ? 11 : 12), position: "relative" }}>
                      <div className="digix-th-sort-row">
                        <SupportSortableHeader columnId="email" label="Email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      </div>
                      <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Training Leads" ? 11 : 12)}></span>
                    </th>
                  )}
                  <th className="col-sticky col-action" style={{ minWidth: getColumnMinWidth(activeTab === "Realestate Leads" ? 17 : activeTab === "Training Leads" ? 12 : 13) || "120px", width: getColumnWidth(activeTab === "Realestate Leads" ? 17 : activeTab === "Training Leads" ? 12 : 13), position: "relative" }}>
                    Action
                    <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, activeTab === "Realestate Leads" ? 17 : activeTab === "Training Leads" ? 12 : 13)}></span>
                  </th>
                </tr>
              </thead>
            <tbody>
              {isSearching ? (
                <tr>
                  <td colSpan={activeTab === "Training Leads" ? "13" : activeTab === "Realestate Leads" ? "19" : activeTab === "Internship Leads" ? "13" : activeTab === "Job Leads" ? "13" : "15"} style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                    Searching...
                  </td>
                </tr>
              ) : freeSearchFilteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === "Training Leads" ? "13" : activeTab === "Realestate Leads" ? "19" : activeTab === "Internship Leads" ? "13" : activeTab === "Job Leads" ? "13" : "15"} style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                    No {activeTab.toLowerCase()} found.
                  </td>
                </tr>
              ) : (
                <>
                  {activeTab === "Training Leads" && (
                    <TrainingLeadsTable
                      rows={currentLeads}
                      courses={courses}
                      domains={domains}
                      servicesData={servicesData}
                      getColumnWidth={getColumnWidth}
                      getColumnMinWidth={getColumnMinWidth}
                      handleView={handleView}
                      handleEdit={canEdit ? handleEdit : undefined}
                      handleDelete={canDelete ? handleDelete : undefined}
                    />
                  )}
                  {activeTab === "Job Leads" && (
                    <JobLeadsTable
                      rows={currentLeads}
                      courses={courses}
                      servicesData={servicesData}
                      domains={domains}
                      getColumnWidth={getColumnWidth}
                      getColumnMinWidth={getColumnMinWidth}
                      handleView={handleView}
                      handleEdit={canEdit ? handleEdit : undefined}
                      handleDelete={undefined}
                    />
                  )}
                  {activeTab === "Realestate Leads" && (
                    <RealestateLeadsTable
                      rows={currentLeads}
                      courses={courses}
                      servicesData={servicesData}
                      domains={domains}
                      getColumnWidth={getColumnWidth}
                      getColumnMinWidth={getColumnMinWidth}
                      handleView={handleView}
                      handleEdit={canEdit ? handleEdit : undefined}
                      handleDelete={undefined}
                      onToggleReFlag={canEdit ? handleToggleReFlag : undefined}
                      leadStatuses={leadStatuses}
                      onUpdateReStatus={canEdit ? handleUpdateReStatus : undefined}
                      onAddReFollowupNote={canEdit ? handleAddReFollowupNote : undefined}
                      selectedReLeadIds={selectedReLeadIds}
                      onToggleReLeadSelection={handleToggleReLeadSelection}
                    />
                  )}
                  {activeTab === "Internship Leads" && (
                    <InternshipLeadsTable
                      rows={currentLeads}
                      courses={courses}
                      servicesData={servicesData}
                      domains={domains}
                      getColumnWidth={getColumnWidth}
                      getColumnMinWidth={getColumnMinWidth}
                      handleView={handleView}
                      handleEdit={canEdit ? handleEdit : undefined}
                      handleDelete={undefined}
                    />
                  )}
                  {activeTab !== "Training Leads" && activeTab !== "Job Leads" && activeTab !== "Internship Leads" && activeTab !== "Realestate Leads" && (
                    <BusinessLeadsTable
                      rows={currentLeads}
                      courses={courses}
                      servicesData={servicesData}
                      domains={domains}
                      getColumnWidth={getColumnWidth}
                      getColumnMinWidth={getColumnMinWidth}
                      handleView={handleView}
                      handleEdit={canEdit ? handleEdit : undefined}
                      handleDelete={undefined}
                    />
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {sortedLeads.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalItems={sortedLeads.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="leads"
          rowsPerPageSelectId="digix-leads-rows-per-page"
        />
      )}
        </div>
      </div>
      {isCallScriptOpen && (
        <div className="call-script-modal-overlay" onClick={() => setIsCallScriptOpen(false)}>
          <div className="call-script-modal" onClick={(e) => e.stopPropagation()}>
            <div className="call-script-modal-header">
              <h3>Digix Leads Call Script</h3>
              <button
                type="button"
                className="call-script-close-btn"
                onClick={() => setIsCallScriptOpen(false)}
                aria-label="Close call script"
              >
                ×
              </button>
            </div>
            <div className="call-script-modal-body">
              {callScriptData.map((item, index) => (
                <div className="call-script-item" key={`${item.speaker}-${index}`}>
                  <p>
                    <strong>{item.speaker}:</strong> {item.text}
                  </p>
                  {item.response && <p className="call-script-response">{item.response}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DigixLeads;
